/**
 * iter971k-D · Global Mono catch-up banner.
 *
 * A single, compact horizontal strip that appears at the very top of every
 * page while any Nigerian bank feed is catching up. State is fetched from
 * the backend (`/api/open-banking-feeds/mono/catch-up-status/current`) so
 * it survives logout/login/tab-close.
 *
 * States:
 *   idle         → renders nothing
 *   in_progress  → blue strip · spinner · "Catching up <bank>… we'll let you know when done"
 *   success      → green strip · check · "<bank> ready" · Review now · Dismiss
 *   failed       → red strip · alert · "Couldn't catch up <bank>" · Retry · Dismiss
 *
 * Design choices per user brief:
 *   - Very small height (~28px), does not push page content much.
 *   - Success state stays pinned until user clicks Review now / Dismiss.
 *   - Failed state only shown after a 15-minute grace window (backend-side).
 *   - Retries route the user back to the bank's picker; a proper Retry
 *     endpoint can be wired later if needed.
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import { Loader2, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import useVisibilityAwareInterval from '../../hooks/useVisibilityAwareInterval';

const POLL_INTERVAL_MS = 8000;          // 8 s
const POLL_INTERVAL_ACTIVE_MS = 4000;   // 4 s while a catch-up is running

function formatBanks(banks) {
  if (!banks || banks.length === 0) return 'your bank';
  if (banks.length === 1) return banks[0];
  if (banks.length === 2) return `${banks[0]} and ${banks[1]}`;
  return `${banks[0]}, ${banks[1]} and ${banks.length - 2} more`;
}

// iter971k-D · Cache the initial poll cadence in module scope so the very
// first render polls at the aggressive (4s) rate - the user is most likely
// to have JUST completed the Mono widget flow so we want the "in_progress"
// state to show up ASAP.
let _lastKnownActive = true;

export default function MonoCatchUpBanner() {
  const navigate = useNavigate();
  const [banner, setBanner] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get(
        '/open-banking-feeds/mono/catch-up-status/current'
      );
      setBanner(data || null);
    } catch {
      // Silently ignore - no banner is safer than a broken banner.
      setBanner(null);
    }
  }, []);

  // F5 · Visibility-aware polling. Interval accelerates to 4 s while a
  // catch-up is in progress, backs off to 8 s otherwise. When the tab is
  // hidden we pause entirely - background tabs used to hammer this endpoint
  // every 4 s forever.
  const inProgress = banner?.state === 'in_progress' || _lastKnownActive;
  if (banner && banner.state !== 'in_progress') _lastKnownActive = false;
  if (banner?.state === 'in_progress') _lastKnownActive = true;
  const interval = inProgress ? POLL_INTERVAL_ACTIVE_MS : POLL_INTERVAL_MS;
  useVisibilityAwareInterval(load, interval);

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await apiClient.post('/open-banking-feeds/mono/catch-up-status/dismiss');
      setBanner(null);
    } catch {
      /* keep banner if dismiss fails - user can retry */
    } finally {
      setDismissing(false);
    }
  };

  const handleReview = () => {
    handleDismiss(); // fire-and-forget dismiss, then navigate
    navigate('/bank-feeds');
  };

  const handleRetry = () => {
    handleDismiss();
    navigate('/banks');
  };

  if (!banner || banner.state === 'idle') return null;

  const bankLabel = formatBanks(banner.banks);

  const bg = {
    in_progress: 'bg-blue-50 border-blue-200 text-blue-800',
    success:     'bg-emerald-50 border-emerald-200 text-emerald-800',
    failed:      'bg-rose-50 border-rose-200 text-rose-800',
  }[banner.state] || 'bg-slate-50 border-slate-200 text-slate-800';

  return (
    <div
      className={`w-full border-b ${bg} text-xs px-3 py-1.5 flex items-center gap-2`}
      data-testid={`mono-catchup-banner-${banner.state}`}
      role="status"
    >
      {/* Icon */}
      <span className="shrink-0 flex items-center">
        {banner.state === 'in_progress' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        {banner.state === 'success'     && <CheckCircle2 className="w-3.5 h-3.5" />}
        {banner.state === 'failed'      && <AlertTriangle className="w-3.5 h-3.5" />}
      </span>

      {/* Message */}
      <span className="flex-1 truncate">
        {banner.state === 'in_progress' && (
          <>
            <span className="font-medium">Catching up {bankLabel}…</span>
            <span className="ml-1 opacity-75">
              We&apos;ll let you know here when done: feel free to keep working.
            </span>
          </>
        )}
        {banner.state === 'success' && (
          <>
            <span className="font-medium">{bankLabel} caught up</span>
            {banner.review_new_bft_count > 0 && (
              <span className="ml-1 opacity-75">
                with {banner.review_new_bft_count} new transaction{banner.review_new_bft_count === 1 ? '' : 's'} ready to review
              </span>
            )}
          </>
        )}
        {banner.state === 'failed' && (
          <>
            <span className="font-medium">Couldn&apos;t catch up {bankLabel}</span>
            <span className="ml-1 opacity-75">
              Please try again from My Banks.
            </span>
          </>
        )}
      </span>

      {/* Actions */}
      <div className="shrink-0 flex items-center gap-1">
        {banner.state === 'success' && (
          <button
            className="underline underline-offset-2 hover:opacity-80 px-1"
            onClick={handleReview}
            data-testid="mono-catchup-review-btn"
          >
            Review now
          </button>
        )}
        {banner.state === 'failed' && banner.can_retry && (
          <button
            className="underline underline-offset-2 hover:opacity-80 px-1"
            onClick={handleRetry}
            data-testid="mono-catchup-retry-btn"
          >
            Retry
          </button>
        )}
        {banner.can_dismiss && (
          <button
            className="hover:opacity-80 p-0.5 rounded"
            onClick={handleDismiss}
            disabled={dismissing}
            aria-label="Dismiss"
            data-testid="mono-catchup-dismiss-btn"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
